import React, { useState, useEffect } from 'react';
import WorkspaceAPI from '../../../../redux/workspace/WorkspaceAPI';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Paper } from '@mui/material';
import useMessage from '../../../../shared/hooks/useMessage';
import useErrorMessage from '../../../../shared/hooks/useErrorMessage';
import WorkspaceInheritanceAPI from '../../../../redux/workspace/WorkspaceInheritance/WorkspaceInheritanceAPI';
import { WorkspaceInheritanceCreateRequest } from '../../../../redux/workspace/WorkspaceInheritance/WorkspaceInheritanceAPI.model';
import UserAPI from '../../../../redux/user/UserAPI';
import useMyWorkspace from '../../../../redux/workspace/hooks/useMyWorkspace';
import {
  WorkspaceDetailForm,
  WorkspaceDetailFormSkeleton,
} from './WorkspaceDetailForm';

export interface ImageHandlerProps {
  image: string;
  isNewUpload: boolean;
  file?: File;
  error?: string;
}
const allowedImageTypes = ['image/png', 'image/jpeg', 'image/jpg'];
const MAX_SIZE_IMAGE_KBYTE = 1024;
const KBYTE = 1024;
export const WorkspaceDetail = () => {
  const { marketId: subworkspaceId } = useParams<{ marketId: string }>();
  const { data: myWorkspace } = useMyWorkspace();

  const { data: myUser, isLoading: isLoadingUser } =
    UserAPI.useGetMyUserQuery();

  const [addImage] = WorkspaceAPI.useUploadImageMutation();
  const [removeImage] = WorkspaceAPI.useRemoveImageMutation();
  const [logoImage, setLogoImage] = useState<ImageHandlerProps | null>(null);

  const { data: workspace, isLoading } = WorkspaceAPI.useGetWorkspaceByIdQuery(
    subworkspaceId || '',
    { skip: !subworkspaceId }
  );

  const [createWorkspace] =
    WorkspaceInheritanceAPI.useCreateWorkspaceMutation();
  const [updateSettings] = WorkspaceAPI.useUpdateSettingsMutation();
  const [updateQuota] = WorkspaceInheritanceAPI.useUpdateQuotaMutation();

  const { t } = useTranslation();
  const showMessage = useMessage();
  const showErrorMessage = useErrorMessage();

  useEffect(() => {
    if (workspace?.images?.logo) {
      setLogoImage({ image: workspace.images.logo, isNewUpload: false });
    }
  }, [workspace]);

  const validationSchema = yup.object({
    name: yup.string().required(t('workspaces.name_required') ?? ''),
    region: yup.string().required(t('workspaces.region_required') ?? ''),
    connectSetup: yup
      .string()
      .required(t('workspaces.connect_setup_required') ?? ''),
    mainContact: yup
      .string()
      .required(t('workspaces.main_contact_required') ?? ''),
    mainContactEmail: yup
      .string()
      .email(t('workspaces.invalid_email') ?? '')
      .required(t('workspaces.main_contact_email_required') ?? ''),
    images: yup.object(),
    brandColor: yup
      .string()
      .required(t('workspaces.brand_color_required') ?? ''),
    numberDevices: yup
      .number()
      .required(t('workspaces.number_devices_required') ?? '')
      .max(
        Number(myWorkspace?.quota.devices) || 0,
        t('workspaces.number_devices_exceed_quota', {
          quota: myWorkspace?.quota.devices || 0,
        }) ?? ''
      ),
    numberUsers: yup
      .number()
      .required(t('workspaces.number_users_required') ?? '')
      .max(
        Number(myWorkspace?.quota.users) || 0,
        t('workspaces.number_users_exceed_quota', {
          quota: myWorkspace?.quota.users || 0,
        }) ?? ''
      ),
  });

  const images = {
    logo: workspace?.images?.logo || null,
    ...workspace?.images,
  };

  const uploadImage = async (
    file: File,
    key: string,
    newWorkspaceId: string | null
  ): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await addImage({
        workspaceId: newWorkspaceId || '',
        key,
        formData,
      }).unwrap();

      return response.url;
    } catch (error) {
      throw error;
    }
  };

  const uploadImages = async (newWorkspaceId: string | null) => {
    const updatedImages = { ...formik.values.images };

    const uploadPromises =
      logoImage && logoImage.isNewUpload && logoImage.file
        ? [
            (async () => {
              try {
                const uploadedUrl = await uploadImage(
                  logoImage.file!,
                  'logo',
                  newWorkspaceId
                );
                updatedImages['logo'] = uploadedUrl;
                return { key: 'logo', url: uploadedUrl };
              } catch (error) {
                return {
                  key: 'logo',
                  url: null,
                  error: 'workspaces.image_upload_failed',
                };
              }
            })(),
          ]
        : [];
    const results = await Promise.all(uploadPromises);
    results.forEach((result) => {
      if (result.error) {
        throw new Error(t('workspaces.image_upload_failed') || '');
      } else {
        formik.setFieldValue(`images.${result.key}`, result.url);
      }
    });
  };

  const createSubworkspace = async (): Promise<string | null> => {
    const { name } = formik.values;
    try {
      if (!subworkspaceId) {
        const newWorkspaceData: WorkspaceInheritanceCreateRequest = {
          workspaceName: name,
          userQuota: Number(formik.values.numberUsers) || 100, // Default value
          deviceQuota: Number(formik.values.numberDevices) || 100, // Default value
          creatorId: myUser?.id || '',
        };

        const newWorkspace = await createWorkspace({
          formData: newWorkspaceData,
          workspaceId: myWorkspace?.id || '',
        }).unwrap();
        return newWorkspace.id;
      }
    } catch (error) {
      throw new Error(
        t('workspaces.workspace_creation_failed', { workspace: name }) || ''
      );
    }

    return null;
  };

  const editSettings = async (
    values: Record<string, any>,
    newWorkspaceId: string | null
  ) => {
    try {
      const settingsValues: Record<string, string> = { ...values };
      delete settingsValues.name;
      delete settingsValues.images;
      delete settingsValues.numberDevices;
      delete settingsValues.numberUsers;

      await updateSettings({
        workspaceId: newWorkspaceId || '',
        formData: settingsValues,
      }).unwrap();
    } catch (error) {
      throw new Error(
        t('workspaces.workspace_update_failed', { workspace: values.name }) ||
          ''
      );
    }
  };
  const setQuota = async (
    quota: { users: number; devices: number },
    newWorkspaceId: string | null
  ) => {
    try {
      await updateQuota({
        workspaceId: myWorkspace?.id || '',
        subworkspaceId: newWorkspaceId || '',
        quota,
      }).unwrap();
    } catch (error) {
      throw new Error(
        t('workspaces.workspace_update_failed', {
          workspace: formik.values.name,
        }) || ''
      );
    }
  };

  const defaultUsers =
    myWorkspace?.quota?.users ?? 0 > 100 ? 100 : myWorkspace?.quota?.users || 0;
  const defaultDevices =
    myWorkspace?.quota?.devices ?? 0 > 100
      ? 100
      : myWorkspace?.quota?.devices || 0;

  const formik = useFormik({
    initialValues: {
      name: workspace?.name || '',
      region: workspace?.settings?.region || '',
      connectSetup: workspace?.settings?.connectSetup || '',
      mainContact: workspace?.settings?.mainContact || '',
      mainContactEmail: workspace?.settings?.mainContactEmail || '',
      brandColor: workspace?.settings?.brandColor || '#000000',
      images,
      numberDevices: workspace?.quota?.devices || defaultDevices,
      numberUsers: workspace?.quota?.users || defaultUsers,
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (!formik.dirty) {
        return;
      }
      let newWorkspaceId = workspace?.id || null;
      try {
        if (!subworkspaceId) {
          const response = await createSubworkspace();
          newWorkspaceId = response;
          if (!newWorkspaceId) {
            throw new Error(
              t('workspaces.workspace_creation_failed', {
                workspace: values.name,
              }) || ''
            );
          }
        }

        if (formik.touched.numberUsers || formik.touched.numberDevices) {
          const quota = {
            users: Number(values.numberUsers),
            devices: Number(values.numberDevices),
          };
          await setQuota(quota, newWorkspaceId);
        }
        await editSettings(values, newWorkspaceId);
        await uploadImages(newWorkspaceId);

        if (!logoImage && workspace?.images?.logo) {
          await removeImage({ workspaceId: newWorkspaceId || '', key: 'logo' });
        }

        showMessage(
          t('workspaces.workspace_updated_successfully', {
            workspace: values.name,
          })
        );
      } catch (error) {
        if (error instanceof Error) {
          showErrorMessage(error.message);
          return;
        }
        showErrorMessage(
          t('workspaces.workspace_update_failed', {
            workspace: values.name,
          })
        );
      }
    },
  });

  const imageHandler = (file: File | undefined) => {
    if (file) {
      if (
        file.type.indexOf('image') === -1 ||
        allowedImageTypes.indexOf(file.type) === -1
      ) {
        setLogoImage({
          error: t('workspaces.invalid_image_format') || '',
          image: '',
          isNewUpload: false,
        });
        return;
      }
      if (file.size > MAX_SIZE_IMAGE_KBYTE * KBYTE) {
        setLogoImage({
          error:
            t('workspaces.invalid_image_size', {
              imageSizeMB: MAX_SIZE_IMAGE_KBYTE / KBYTE,
            }) || '',
          image: '',
          isNewUpload: false,
        });
        return;
      }
      const imageUrl = URL.createObjectURL(file);
      setLogoImage({
        image: imageUrl,
        isNewUpload: true,
        file: file,
      });

      formik.setFieldValue('images.logo', imageUrl);
    }
  };

  if (subworkspaceId && isLoading && isLoadingUser) {
    return <WorkspaceDetailFormSkeleton />;
  }

  return (
    <Paper elevation={5} sx={{ padding: 3, maxWidth: 'xl', margin: 'auto' }}>
      <WorkspaceDetailForm
        formik={formik}
        imageHandler={imageHandler}
        quota={workspace?.quota || { users: 100, devices: 100 }}
        logoImage={logoImage}
        setLogoImage={setLogoImage}
        subworkspaceId={subworkspaceId}
      />
    </Paper>
  );
};
